import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import "../servicesOperation.css";
import { ModalConfirmEventos_ServicesOperation } from "views/Components/ModalConfirmEventos_Services_Operation.js";
import Animation_Success_animation from "../Helper/Animation_Success_animation.js";
import { Alerta_Permisos_Services_Operation } from "views/Components/Alerta_Permisos_Services_Operation.js";
import Animation_Warning_animation from "../Helper/Animation_Warning.js";
import Animation_Error_animation from "../Helper/Animation_Error.js";
import useAuth from "hooks/useAuth";
import { useDispatch } from "react-redux";
import { activeItem } from "store/slices/menu.js";
import { Typography } from "@mui/material";

const baseUrl = process.env.REACT_APP_BASE_URL;

const DragAndDrop = ({ url, country }) => {
  // Obtenemos la fecha de ahora
  const navigate = useNavigate(); // Obtiene la función de navegación
  const dispatch = useDispatch();
  const { accessToken } = useAuth();

  //Estados de manejo de error
  // const [open, setOpenModalError] = useState(false); // Controla si el Snackbar está abierto
  // const [errorMessage, setMessageError] = useState(''); // Mensaje de error para mostrar

  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalWarning, setOpenModalWarning] = useState(false);
  const [openModalWarningEmptyFile, setOpenModalWarningEmptyFile] =
    useState(false);
  const [openModalError, setOpenModalError] = useState(false);

  const [messageWarning, setMessageWarning] = useState("");
  const [messageWarningEmptyFile, setMessageWarningEmptyFile] = useState("");
  const [messageError, setMessageError] = useState("");

  //Estados de manejo de alerta OK
  const [resultData, setResultData] = useState({
    message: "",
    registrosDuplicados: 0,
    registrosExitosos: 0,
    registrosProcesados: 0,
    totalRegistros: 0,
  });

  //Función clickeable para ocultar mensaje alerta
  const handleCloseModal = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setOpenModalSuccess(false);
    navigate("/service-operation/admin");
    dispatch(activeItem(["ServiceOperationAdmin"]));
  };

  const handleCloseModalWarning = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setOpenModalWarning(false);
  };
  const handleCloseModalWarningEmptyValue = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setOpenModalWarningEmptyFile(false);
  };
  const handleCloseModalError = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setOpenModalError(false);
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(async (file) => {
      const formdata = new FormData();
      formdata.append("file", file, file.name);
      formdata.append("countryId", country.id);

      try {
        const respuesta = await fetch(url, {
          method: "POST",
          body: formdata,
          redirect: "follow",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            accept: "*/*",
          },
        });

        const resultado = await respuesta.json();

        if (respuesta.ok) {
          setResultData({
            success: true,
            message: resultado.message,
            registrosDuplicados: resultado.registrosDuplicados,
            registrosExitosos: resultado.registrosExitosos,
            registrosProcesados: resultado.registrosProcesados,
            totalRegistros: resultado.totalRegistros,
          }); // Asume que el servidor responde con un mensaje en caso de éxito

          if (resultado.registrosDuplicados > 0) {
            setMessageWarning(
              `De los N°${resultado.registrosProcesados} registros procesados, N°${resultado.registrosDuplicados} son duplicados`,
            );
            setOpenModalWarning(true);
          } else if (resultado.registrosProcesados === 0) {
            //openModalWarningEmptyFile, setOpenModalWarningEmptyFile
            setMessageWarningEmptyFile(
              `El archivo que se cargó se encuentra vacío o sin registros`,
            );
            setOpenModalWarningEmptyFile(true);
          } else {
            setOpenModalSuccess(true);
          }
        } else {
          // Si hubo un problema con el envío, muestra un mensaje de error
          setMessageError(
            "Ocurrió un error inesperado. Por favor, comuníquese con soporte.",
          );
          //setMessageError(resultado.message);
          setOpenModalError(true);
          return { success: false, message: resultado.message }; // Asume que el servidor responde con un mensaje de error
        }

        // Aquí puedes manejar la respuesta exitosa, por ejemplo, mostrando un mensaje al usuario
      } catch (error) {
        // Aquí puedes manejar el error, por ejemplo, mostrando un mensaje al usuario
        setMessageError(
          "Por favor, ingrese un excel válido. Si el problema persiste, comuníquese con soporte.",
        );
        setOpenModalError(true);
      }
    });
  }, []);

  // useDropzone es un hook que nos proporciona react-dropzone. Lo configuramos para aceptar archivos .xlsx y .xls
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".xlsx, .xls",
    maxSize: 1 * 1024 * 1024, // 1 MB
    onDropRejected: (fileRejections) => {
      // Inicializamos una variable para almacenar los mensajes de error
      let errorMessage = "";
      fileRejections.forEach((fileRejection) => {
        fileRejection.errors.forEach((error) => {
          switch (error.code) {
            case "file-invalid-type":
              errorMessage +=
                "El tipo de archivo no es válido. Solo se permiten archivos Excel.";
              break;
            case "file-too-large":
              errorMessage +=
                "El archivo es demasiado grande. El tamaño máximo permitido es 1 MB.";
              break;
            // Valor por defecto
            default:
              errorMessage += "El archivo no cumple con los requisitos. ";
              break;
          }
        });
      });
      // Establecemos el mensaje de error y mostramos el Snackbar
      setMessageError(errorMessage.trim());
      setOpenModalError(true);
    },
  });

  // Renderizamos la zona de drop. getRootProps y getInputProps son funciones que proporciona react-dropzone
  // para vincular las propiedades necesarias al div y al input, respectivamente.
  return (
    <div
      {...getRootProps()}
      style={{
        border: "2px dashed #ccc",
        padding: "20px",
        margin: "20px 0px",
        height: "75%",
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: "400px",
        maxHeight: "500px",
      }}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <>
          <div
            style={{
              display: "flex",
              "align-content": "center",
              "flex-wrap": "nowrap",
              "justify-content": "center",
              "align-items": "center",
              "flex-direction": "column",
            }}
          >
            <CloudDownloadIcon />
            <p>Suelta el archivo aquí...</p>
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              "align-content": "center",
              "flex-wrap": "nowrap",
              "justify-content": "center",
              "align-items": "center",
              "flex-direction": "column",
              gap: "16px",
            }}
          >
            <CloudUploadIcon />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "24px",
                color: "#000000DE",
                lineHeight: "24px",
              }}
            >
              Arrastra un archivo aquí, o haz click para seleccionarlo.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize: "16px",
                color: "#00000099",
                lineHeight: "24px",
              }}
            >
              Formatos aceptados: .xlsx, .csv · Máx 10 MB
            </Typography>
            <Button
              variant="contained"
              style={{
                backgroundColor: "#002878",
                "&:hover": { backgroundColor: "#002878" },
              }}
            >
              Seleccionar archivo
            </Button>
          </div>
        </>
      )}

      <ModalConfirmEventos_ServicesOperation
        open={openModalSuccess}
        CerrarModal={handleCloseModal}
        icon={<Animation_Success_animation />}
        success={true}
        message={`Se subieron los datos correctamente! N° ${resultData.registrosExitosos} archivos!`}
        message2={`Hay un total de N°${resultData.totalRegistros} cargados`}
      />
      <Alerta_Permisos_Services_Operation
        open={openModalWarning}
        CerrarModal={handleCloseModalWarning}
        icon={<Animation_Warning_animation />}
        message="¡A tener en cuenta!"
        message2={messageWarning}
      />
      <Alerta_Permisos_Services_Operation
        open={openModalWarningEmptyFile}
        CerrarModal={handleCloseModalWarningEmptyValue}
        icon={<Animation_Warning_animation />}
        message="¡A tener en cuenta!"
        message2={messageWarningEmptyFile}
      />
      <Alerta_Permisos_Services_Operation
        open={openModalError}
        CerrarModal={handleCloseModalError}
        icon={<Animation_Error_animation />}
        message="¡Ups! Algo no salió como esperábamos"
        message2={messageError}
      />
    </div>
  );
};

export default DragAndDrop;
